import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CreateExaminationRequest,
  DeleteExaminationRequest,
  ExaminationFormResponse,
  ExaminationFormsResponse,
  ExaminationResponse,
  ExaminationsResponse,
  GetDepartmentExaminationFormsRequest,
  GetDepartmentExaminationsRequest,
  GetUserExaminationFormsRequest,
  GetUserExaminationsRequest,
  UpdateExaminationRequest,
} from '@pixacare/dtos';
import { FormAnswers } from '@pixacare/forms';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Cacheable } from 'ts-cacheable';

@Injectable({
  providedIn: 'root',
})
export class ExaminationsHttpService {
  private readonly http = inject(HttpClient);

  @Cacheable()
  getUserExaminationForms({
    clientCode,
    withForms,
  }: GetUserExaminationFormsRequest): Observable<ExaminationFormResponse[]> {
    return this.http.get<ExaminationFormsResponse>(`${environment.apiBaseUrl}/examinationform/${clientCode}`, {
      params: { withForms },
    });
  }

  @Cacheable()
  getDepartmentExaminationForms({
    clientCode,
    departmentId,
    withForms,
  }: GetDepartmentExaminationFormsRequest): Observable<ExaminationFormsResponse> {
    return this.http.get<ExaminationFormsResponse>(
      `${environment.apiBaseUrl}/examinationform/${clientCode}/department/${departmentId}`,
      { params: { withForms } },
    );
  }

  createExamination({
    clientCode,
    examinationFormId,
    patientId,
    formAnswers,
  }: CreateExaminationRequest): Observable<ExaminationResponse> {
    return this.http.post<ExaminationResponse>(
      `${environment.apiBaseUrl}/patient/${clientCode}/${patientId}/examination`,
      this.createFormData(formAnswers, examinationFormId),
    );
  }

  getUserExaminations({
    clientCode,
    patientId,
    withForms = false,
  }: GetUserExaminationsRequest): Observable<ExaminationsResponse> {
    return this.http.get<ExaminationsResponse>(
      `${environment.apiBaseUrl}/patient/${clientCode}/${patientId}/examination`,
      { params: { withForms } },
    );
  }

  getDepartmentExaminations({
    clientCode,
    departmentId,
    patientId,
    withForms = false,
  }: GetDepartmentExaminationsRequest): Observable<ExaminationsResponse> {
    return this.http.get<ExaminationsResponse>(
      `${environment.apiBaseUrl}/patient/${clientCode}/${patientId}/department/${departmentId}/examination`,
      { params: { withForms } },
    );
  }

  deleteExamination({ clientCode, patientId, examinationId }: DeleteExaminationRequest): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiBaseUrl}/patient/${clientCode}/${patientId}/examination/${examinationId}`,
    );
  }

  updateExamination({
    clientCode,
    patientId,
    examinationId,
    formAnswers,
  }: UpdateExaminationRequest): Observable<ExaminationResponse> {
    return this.http.patch<ExaminationResponse>(
      `${environment.apiBaseUrl}/patient/${clientCode}/${patientId}/examination/${examinationId}`,
      this.createFormData(formAnswers),
    );
  }

  private createFormData(formAnswers: FormAnswers, examinationFormId?: number): FormData {
    const formData = new FormData();
    formData.append('formAnswer', JSON.stringify(formAnswers));
    if (examinationFormId) {
      formData.append('examinationFormId', examinationFormId.toString());
    }
    return formData;
  }
}
